import {
    allVariants,
    collection,
    parentTypeFor,
    routePath,
} from "../_variants/index.js";
import {
    completeEadt,
    editAEHAssessment,
    editDesignStandards,
    editEadtData,
    editEquipmentSpecs,
    editFinalSection,
    editLimitations,
    editSimplifiedEADT,
    editSoftwareAssessment,
    envQualAttachable,
    reviseEadt,
    sectionAFields
} from "./actions";

import EadtItemView from "./EadtItemView.vue";
import ItemListView from "../../../components/ItemListView/ItemListView.vue";
import internalAttachables from "./internalAttachables";
import partNumberAttachable from "../ApprovedPartNumbers/attachable.js";
import { loadEadtLinkedData } from "./eadtLinkedData.js";

const makeDatasheetsAttachable = (variant) => {
    const docCollection = collection(variant, "EADTs");
    return {
        "label": "Datasheets",
        "key": "Datasheets",
        "local": true,
        "headers": [{
            "headerName": "File",
            "field": "File",
        }],
        "actions": [{
            "label": "Attach Datasheet",
            action({ form, pageItem }) {
                form.setConfig({
                    "title": "Attach Datasheet",
                    "type": "push",
                    "location": `${docCollection}/${pageItem.ID}/Datasheets`,
                    "fields": [{
                        "label": "Datasheet",
                        "name": "File",
                        "type": "file"
                    }],
                    preSubmitHook({ files }) {
                        return {
                            "File": files[0][0].name
                        };
                    },
                    async postSubmitHook({ files, db, typeRef }) {
                        if (files.length > 0 && files[0].length < 1) return;
                        for (const file of files) {
                            await db.upload(`${docCollection}/${pageItem.ID}/Datasheet`, file[0]);
                        }
                    }
                });
                form.toggle();
            }
        }],
        "rowActions": [{
            "label": "Download",
            async action({ pageItem, rowItem, db }) {
                const url = await db.download(`${docCollection}/${pageItem.ID}/Datasheet/${rowItem.File}`);
                window.open(url, "_blank");
            }
        }, {
            "label": "Delete",
            async action({ form, pageItem, rowItem, db, toast }) {
                try {
                    // form.setConfig({
                    //     "title": "Remove File",
                    //     "submitButtonLabel": "Remove",
                    //     "submitButtonClass": "cancel",
                    //     "type": "delete",
                    //     "location": `Audits/Reports/${pageItem.ID}/Files/${rowItem.ID}`,
                    //     "fields": [],
                    //     async postSubmitHook({ db, typeRef, files }) {
                    //         for (const file of files) {
                    //             await db.remove(`Audits/Reports/${pageItem.ID}/Files/${rowItem.ID}`, rowItem.File);
                    //         }
                    //     }
                    // });
                    form.setConfig({
                        "title": "Delete Datasheet",
                        "submitButtonLabel": "Delete",
                        "submitButtonClass": "cancel",
                        "type": "delete",
                        "location": `${docCollection}/${pageItem.ID}/Datasheets/${rowItem.ID}`,
                        "fields": [],
                    });
                    form.toggle();
                    
                    
                } catch (err) {
                    toast.error("Error deleting datasheet");
                }
            }
        }]
    };
};

/**
 * Build the list + detail vue-router routes for the EADT register matching
 * the supplied variant.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeEADTRoutes = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military EADT" : "EADT";
    const labelPlural = isMilitary ? "Military EADTs" : "EADTs";
    const eadtParentType = parentTypeFor(variant, "EADTs");
    const listPath = routePath(variant, "EADTs");
    const datasheetsAttachable = makeDatasheetsAttachable(variant);

    return [{
        "path": listPath,
        "component": { ...ItemListView },
        "name": labelPlural,
        "meta": {
            "app": variant.app,
            "name": labelPlural,
            "sideNav": true,
            "sideNavSection": "Equipment Assessments",
            "appSwitcher": false,
            "icon": variant.appIcon,
            "tableSettings": "Documents",
            "dataPath": collection(variant, "EADTs"),
            "selectionRoute": `Database/${eadtParentType}/{ID}`,
            filter(item) {
                return item.Number.includes(".EADT-");
            }
        }
    }, {
        "path": `${listPath}/:id`,
        "component": EadtItemView,
        "name": labelSingular,
        "meta": {
            "app": variant.app,
            "name": labelSingular,
            "dataPathOverride": `${collection(variant, "EADTs")}/:id`,
            title(item) {
                return `${item.Number} - Revision ${item.Revision}`;
            },
            "menuActions": [{
                "label": "Edit Section A",
                "action": editEadtData,
                show({ pageItem }) {
                    if(pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.Completed !== true, "EADT is completed."];
                }
            }, {
                "label": "Edit Simplified EADT",
                "action": editSimplifiedEADT,
                show({ pageItem }) {
                        if(pageItem.Completed === true) {
                            return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                        }
                    return [pageItem.EADTClassification === "Simplified", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit Design Standards",
                "action": editDesignStandards,
                show({ pageItem }) {
                    if (pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.EADTClassification === "Detailed", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit Equipment Specifications",
                "action": editEquipmentSpecs,
                show({ pageItem }) {
                    if (pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.EADTClassification === "Detailed", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit Software Assessment",
                "action": editSoftwareAssessment,
                show({ pageItem }) {
                    if (pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.EADTClassification === "Detailed", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit AEH Assessment",
                "action": editAEHAssessment,
                show({ pageItem }) {
                    if (pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.EADTClassification === "Detailed", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit Additional Data",
                "action": editFinalSection,
                show({ pageItem }) {
                    if (pageItem.Completed === true) {
                        return [false, "You cannot edit a complete EADT, you must revise it before it is editing."];
                    }
                    return [pageItem.EADTClassification === "Detailed", "Not available for this EADT classification."];
                }
            }, {
                "label": "Edit Equipment Limitations",
                "action": editLimitations,
                show({ pageItem }) {
                    
                    return [pageItem.Completed !== true, "EADT is completed."];
                }
            }, {
                "break": true,
                "label": "Complete EADT",
                "action": completeEadt,
                show({ pageItem }) {
                    return [pageItem.Completed !== true, "EADT is completed."];
                }
            }, {
                "break": true,
                "label": "Revise EADT",
                action: reviseEadt
            }],
            "dataFields": [{
                "label": "Number",
                "key": "Number"
            }, {
                "label": "Parent",
                "key": "Parent",
                "link": "Database/:ParentType/:ParentID"
            }, {
                "label": "Is Completed",
                "key": "Completed",
                "type": "bool"
            }, {
                "label": "Date Completed",
                "key": "Date.Completed",
                "type": "date"
            }, {
                "label": "EADT Classification",
                "key": "EADTClassification"
            }, {
                "label": "Failure Classification",
                "key": "FailureClassification"
            }, {
                "label": "Title",
                "key": "Title",
                "class": "md:col-span-3"
            }, ...sectionAFields],
            "formMetadata": {
                "ParentType": eadtParentType
            },
            "attachedItems": {
                "Items": {
                    "attachables": [datasheetsAttachable, partNumberAttachable, envQualAttachable]
                },
                ...internalAttachables
            },
            async getLinkedData({ db, pageItem }) {
                return loadEadtLinkedData(db, pageItem);
            }
        }
    }];
};

export default allVariants().flatMap(makeEADTRoutes);
